import type { OfflineAuthCrypto } from '@shared/offline';
import {
  createCredentialVerifier,
  defaultOfflineAuthCrypto,
  unsealRefreshToken,
  verifyCredential,
} from '@shared/offline';
import type { UserRole } from '../domain/models';

function trySafeStorage(): {
  encryptString(value: string): string;
  decryptString(encrypted: string): string;
  isEncryptionAvailable(): boolean;
} | null {
  try {
    const w = window as unknown as { require?: NodeRequire };
    if (typeof w.require !== 'function') return null;
    const { safeStorage } = w.require('electron') as {
      safeStorage?: {
        isEncryptionAvailable(): boolean;
        encryptString(value: string): Buffer;
        decryptString(encrypted: Buffer): string;
      };
    };
    if (!safeStorage?.isEncryptionAvailable()) return null;
    return {
      isEncryptionAvailable: () => safeStorage.isEncryptionAvailable(),
      encryptString: (value: string) => safeStorage.encryptString(value).toString('base64url'),
      decryptString: (encrypted: string) =>
        safeStorage.decryptString(Buffer.from(encrypted, 'base64url')),
    };
  } catch {
    return null;
  }
}

const safeStorage = trySafeStorage();
const SAFE_PREFIX = 'safe:v1:';

export function createElectronOfflineAuthCrypto(): OfflineAuthCrypto {
  if (!safeStorage) {
    return defaultOfflineAuthCrypto;
  }

  return {
    createCredentialVerifier,
    verifyCredential,
    async sealRefreshToken(token: string) {
      return `${SAFE_PREFIX}${safeStorage.encryptString(token)}`;
    },
    async unsealRefreshToken(sealed: string) {
      if (!sealed.startsWith(SAFE_PREFIX)) {
        return unsealRefreshToken(sealed);
      }
      try {
        return safeStorage.decryptString(sealed.slice(SAFE_PREFIX.length));
      } catch {
        return null;
      }
    },
  };
}

export function userToOfflineSnapshot(user: {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  branchId: string;
  email?: string;
}) {
  return {
    visibleId: user.id,
    username: user.username,
    firstName: user.firstName,
    lastName: user.lastName,
    role: String(user.role),
    branchId: user.branchId,
    email: user.email,
  };
}

export function offlineProfileToUser(profile: {
  user: {
    visibleId: string;
    username: string;
    firstName: string;
    lastName: string;
    role: string;
    branchId: string;
    email?: string;
  };
}) {
  return {
    id: profile.user.visibleId,
    username: profile.user.username,
    email: profile.user.email ?? '',
    firstName: profile.user.firstName,
    lastName: profile.user.lastName,
    role: profile.user.role as UserRole,
    branchId: profile.user.branchId,
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}
